import React, { useState, useEffect, useRef, lazy, Suspense, useReducer, Reducer, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate, Navigate } from "react-router-dom";
import "mta-rkvr-styles/assets/css/mta_visuaal.css";
import "mta-rkvr-styles/assets/css/print.css";
import "mta-rkvr-styles/assets/css/mta_visuaal-extra.css";
import "mta-rkvr-styles/style.css";
import { NavbarHeader, GlobalMenu, Container, Footer, Sidebar, Loader } from "mta-rkvr-styles";
import { getSidebarItems } from "./sidebarItems";
import globalMenuItems, {
  AdministrationMenuItem,
  NotificationsSubscriptionsMenuItem,
  RealEstateUnitMenuItem,
} from "./globalMenuItems";
import Roles2, { logout } from "./temp/Roles2";
import HomePageUnauth from "./homepage/HomePageUnauth";
import GlobalMenuEnum from "./globalMenuEnum";
import packageJson from "../package.json";
import ScrollToTop from "./util/scrollToTop";
import { get, AuthResponseBody, AccountResponse, ConfigResponseBody, LinkPair, post } from "./api/fetch";
import { CurrentUserProvider } from "./CurrentUserContext";
import HomePageAuth from "./homepage/HomePageAuth";
import { AdditionalConfiguration, InfobuttonTextsContainer, UserRoleType, PublicConfigType } from "./types/admin/User";
import SessionTimeoutModal from "./SessionTimeoutModal";
import {
  ADMINISTRATION,
  ASSETS,
  CONTRACTS,
  NOTIFICATIONSSUBSCRIPTIONS,
  PROCEEDINGS,
  MISSING_USER,
  REPORTS,
  REALESTATEUNIT,
  UNIT_MANAGEMENT,
} from "./util/paths";
import NotificationsSubscription from "./notificationsSubscriptions/NotificationsSubscription";
import { canRoleView as canRoleViewAdministration } from "./util/rightsManagers/admin/viewRightsManager";
import { SubviewProvider } from "./SubviewContext";
import { Classifier } from "./util/classifierManager";
import { ErrorPage } from "./common/ErrorPage";
import { userCanAccessRealEstateUnit } from "./util/rightsManagers/realEstateUnit/editRightsManager";
import { Infotext, InfotextQueryBody, InfotextResponse } from "./api/InfobuttonTextsTypes";
import Sitemap from "./Sitemap";

const RealEstateUnitsSearchRouting = lazy(() => import("./realEstateUnits/RealEstateUnitsSearchRouting"));
const AdminRouting = lazy(() => import("./admin/AdminRouting"));
const ReportsRouting = lazy(() => import("./reports/ReportsRouting"));
const ContractsContextProvider = lazy(() => import("./contracts/ContractsContextProvider"));
const ProceedingsRouting = lazy(() => import("./proceedings/ProceedingsRouting"));
const AssetsRouting = lazy(() => import("./assets/AssetRouting"));
const UnitManagementExtRouting = lazy(() => import("./realEstateUnits/UnitManagementExtRouting"));

const { frontEndBase, backEndBase } = window;

function App() {
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = (canToggle: boolean, isToggled: boolean | object) => {
    if (canToggle) {
      if (typeof isToggled === "boolean") {
        setSidebarOpen(isToggled);
      } else {
        setSidebarOpen(!isSidebarOpen);
      }
    }
  };

  useEffect(() => {
    const closeSidebar = (e: any) => {
      if (
        isSidebarOpen &&
        !["navbar", "sidebar", "global-menu"].some((className) => e.target.className.includes(className))
      ) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("click", closeSidebar);

    return () => window.removeEventListener("click", closeSidebar);
  });

  const [selectedTopic, setSelectedTopic] = useState(0);
  const selectGlobalMenuLink = (link: GlobalMenuEnum) => {
    setSelectedTopic(link);
  };
  const userMissingRef = useRef(false);

  const [publicConfig, setPublicConfig] = useState<PublicConfigType | undefined>();

  const [footerLinks, setFooterLinks] = useState<LinkPair[]>();
  useEffect(() => {
    get<ConfigResponseBody>({ endPoint: "configuration" }).then((resp) => {
      if (resp.ok) {
        let ft = resp.body.jalus;
        if (ft) {
          ft.push({ link: frontEndBase + "/sisukaart", text: "Sisukaart" });
          ft = ft.map((r, index) => {
            r.id = index;
            return r;
          });
        }
        setFooterLinks(ft);
        setPublicConfig({
          ehrLink: resp.body.ehrLink,
          krLink: resp.body.krLink,
        });
      }
    });
  }, []);

  return (
    <div className="App container-component-wrapper">
      <div className="page-wrapper">
        <Router basename={frontEndBase}>
          <Routing
            {...{
              isSidebarOpen,
              toggleSidebar,
              setSidebarOpen,
              selectGlobalMenuLink,
              selectedTopic,
              setSelectedTopic,
              userMissingRef,
              publicConfig,
            }}
          />
        </Router>
      </div>
      <Footer versionNr={packageJson.version} footerLinks={footerLinks} />
    </div>
  );
}
//
const Routing: React.FC<any> = ({
  isSidebarOpen,
  toggleSidebar,
  setSidebarOpen,
  selectGlobalMenuLink,
  selectedTopic,
  setSelectedTopic,
  userMissingRef,
  publicConfig,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [refresh, setRefresh] = useState(false);
  const toggleRefresh = () => {
    setRefresh(!refresh);
  };
  const [isLoading, setLoading] = useState(false);
  const [isLoggedIn, setLoggedIn] = useState(false);

  // If popover is open and user navigates to another page, remove popover immediately; otherwise it stays on screen until the next route finishes loading.
  useEffect(() => {
    const popovers = document.querySelectorAll(".popover");
    popovers.forEach((el) => el.remove());
  }, [pathname]);

  useEffect(() => {
    if (pathname == "/" + MISSING_USER) {
      userMissingRef.current = true;
    }
  }, []);
  const [user, setUser] = useState<{
    account: AccountResponse;
    authenticate: () => void;
    isDevloginEnabled: boolean;
  }>();

  const [additionalConf, setAdditionalConf] = useState<AdditionalConfiguration>();

  const [infotexts, setInfotexts] = useReducer<Reducer<InfobuttonTextsContainer, Partial<InfobuttonTextsContainer>>>(
    (a: InfobuttonTextsContainer, b: Partial<InfobuttonTextsContainer>) => ({
      loadedComplex: b.loadedComplex == true && !a.loadedComplex ? b.loadedComplex : a.loadedComplex,
      loadedUnitManagement:
        b.loadedUnitManagement == true && !a.loadedUnitManagement ? b.loadedUnitManagement : a.loadedUnitManagement,
      complexTexts: b.loadedComplex == true && !a.loadedComplex ? b.complexTexts : a.complexTexts,
      unitManagementTexts:
        b.loadedUnitManagement == true && !a.loadedUnitManagement ? b.unitManagementTexts : a.unitManagementTexts,
      loadedIpTexts: b.loadedIpTexts == true && !a.loadedIpTexts ? b.loadedIpTexts : a.loadedIpTexts,
      investmentProjectTexts:
        b.loadedIpTexts == true && !a.loadedIpTexts ? b.investmentProjectTexts : a.investmentProjectTexts,
      loadedIpListTexts:
        b.loadedIpListTexts == true && !a.loadedIpListTexts ? b.loadedIpListTexts : a.loadedIpListTexts,
      investmentListTexts:
        b.loadedIpListTexts == true && !a.loadedIpListTexts ? b.investmentListTexts : a.investmentListTexts,
      loadedIpSearchTexts:
        b.loadedIpSearchTexts == true && !a.loadedIpSearchTexts ? b.loadedIpSearchTexts : a.loadedIpSearchTexts,
      investmentSearchTexts:
        b.loadedIpSearchTexts == true && !a.loadedIpSearchTexts ? b.investmentSearchTexts : a.investmentSearchTexts,
      uploadTexts: b.loadedUploadTexts == true && !a.loadedUploadTexts ? b.uploadTexts : a.uploadTexts,
      loadedUploadTexts:
        b.loadedUploadTexts == true && !a.loadedUploadTexts ? b.loadedUploadTexts : a.loadedUploadTexts,
      loadedAddressTexts:
        b.loadedAddressTexts == true && !a.loadedAddressTexts ? b.loadedAddressTexts : a.loadedAddressTexts,
      addressTexts: b.loadedAddressTexts == true && !a.loadedAddressTexts ? b.addressTexts : a.addressTexts,
      loadedAdminTexts: b.loadedAdminTexts == true && !a.loadedAdminTexts ? b.loadedAdminTexts : a.loadedAdminTexts,
      adminTexts: b.loadedAdminTexts == true && !a.loadedAdminTexts ? b.adminTexts : a.adminTexts,
    }),
    {
      loadedComplex: false,
      loadedUnitManagement: false,
      loadedIpTexts: false,
      loadedIpListTexts: false,
      loadedIpSearchTexts: false,
      loadedUploadTexts: false,
      loadedAddressTexts: false,
      loadedAdminTexts: false,
    },
  );

  const updateInfotexts = (
    whichUpdate:
      | "ADDRESS"
      | "ADMIN"
      | "COMPLEX"
      | "UNIT_MANAGEMENT"
      | "INVESTMENT"
      | "INVESTMENT_LIST"
      | "INVESTMENT_SEARCH"
      | "UPLOAD",
    nVals: Infotext[],
  ) => {
    const nInfotext: InfobuttonTextsContainer = {
      loadedComplex: false,
      loadedUnitManagement: false,
      loadedIpTexts: false,
      loadedIpListTexts: false,
      loadedIpSearchTexts: false,
      loadedUploadTexts: false,
      loadedAddressTexts: false,
      loadedAdminTexts: false,
    };
    if (whichUpdate === "COMPLEX") {
      nInfotext.complexTexts = nVals;
      nInfotext.loadedComplex = true;
    } else if (whichUpdate === "UNIT_MANAGEMENT") {
      nInfotext.unitManagementTexts = nVals;
      nInfotext.loadedUnitManagement = true;
    } else if (whichUpdate === "ADDRESS") {
      nInfotext.addressTexts = nVals;
      nInfotext.loadedAddressTexts = true;
    } else if (whichUpdate === "INVESTMENT") {
      nInfotext.investmentProjectTexts = nVals;
      nInfotext.loadedIpTexts = true;
    } else if (whichUpdate === "INVESTMENT_LIST") {
      nInfotext.investmentListTexts = nVals;
      nInfotext.loadedIpListTexts = true;
    } else if (whichUpdate === "INVESTMENT_SEARCH") {
      nInfotext.investmentSearchTexts = nVals;
      nInfotext.loadedIpSearchTexts = true;
    } else if (whichUpdate === "UPLOAD") {
      nInfotext.uploadTexts = nVals;
      nInfotext.loadedUploadTexts = true;
    } else if (whichUpdate === "ADMIN") {
      nInfotext.adminTexts = nVals;
      nInfotext.loadedAdminTexts = true;
    }
    setInfotexts(nInfotext);
  };

  const [currentGlobalMenuItems, setGlobalMenuItems] = useState(globalMenuItems);

  useEffect(() => {
    if (user == undefined || infotexts.loadedAddressTexts) {
      return;
    }
    post<InfotextQueryBody, InfotextResponse>({
      endpoint: "infotexts",
      requestBody: {
        onTekst: true,
        vaated: ["aadressiotsing"],
      },
    }).then((resp) => {
      if (resp.ok) {
        updateInfotexts("ADDRESS", resp.body.resultsList);
      } else {
        console.log("Failed to load infotexts");
      }
    });
  }, [user]);

  useEffect(() => {
    setLoading(true);
    setLoggedIn(false);
    get<AuthResponseBody>({ endPoint: "authInfo" }).then((res) => {
      const authenticate = () => {
        window.location.href = `${backEndBase}${res.body.authUrl}`;
      };
      window.authenticate = authenticate;
      if (res.ok && res.body.isAuthenticated) {
        get<ConfigResponseBody>({ endPoint: "configuration" }).then((resp) => {
          if (resp.ok) {
            setAdditionalConf({
              allowedFileExtension: resp.body.lubatudFailiLaiendid,
              jkStrategyLink: resp.body.jkStrateegiaLink,
              countryCodesLink: resp.body.riikideKlassifikaator,
              adsOidLink: resp.body.adsOidLink,
            });
          }
        });
        setUser({
          account: res.body.account,
          authenticate,
          isDevloginEnabled: res.body.isDevloginEnabled,
        });
        setLoggedIn(true);
        incrementAndGet();

        const menuItemsCopy = [...globalMenuItems];
        if (
          res.body.account &&
          userCanAccessRealEstateUnit(
            res.body.account?.kasutusoigused?.map((oigus) => UserRoleType[oigus as keyof typeof UserRoleType]),
          )
        ) {
          menuItemsCopy.push(RealEstateUnitMenuItem);
        }

        menuItemsCopy.push(NotificationsSubscriptionsMenuItem);
        if (
          res.body.account &&
          canRoleViewAdministration(
            res.body.account?.kasutusoigused?.map((oigus) => UserRoleType[oigus as keyof typeof UserRoleType]),
          )
        ) {
          menuItemsCopy.splice(6, 0, AdministrationMenuItem);
        }

        setGlobalMenuItems(menuItemsCopy);
      } else {
        setUser({
          account: {
            esindusoigused: [],
            id: -1,
            isikukood: "",
            kasutusoigused: [],
            huviorganisatsioonid: [],
            nimi: "Rolliõiguseta Kasutaja",
          },
          authenticate,
          isDevloginEnabled: res.body.isDevloginEnabled,
        });
        setLoggedIn(false);
        setAdditionalConf(undefined);
        incrementAndGet();
      }
      setLoading(false);
    });
  }, [refresh]);

  const [lastError, setLastError] =
    useState<
      { code?: number | string; timestamp?: string; request?: string; uniquecode?: string; queryContext?: string }[]
    >();

  const addError = useCallback(
    (newErr: {
      code?: number | string;
      timestamp?: string;
      request?: string;
      uniquecode?: string;
      queryContext?: string;
    }) => {
      setLastError((prevState) => (prevState ? [...prevState, newErr] : [newErr]));
    },
    [setLastError],
  );

  useEffect(() => {
    const toErrorPage = (
      code?: number | string,
      timestamp?: string,
      request?: string,
      uniquecode?: string,
      queryContext?: string,
    ) => {
      addError({ code, timestamp, request, uniquecode, queryContext });
    };
    window.toErrorPage = toErrorPage;

    const toWelcomePage = () => {
      navigate("/"); // redirects to welcomepage
    };
    window.toWelcomePage = toWelcomePage;
  }, [addError]);

  useEffect(() => {
    if (lastError) {
      setLastError(undefined);
    }
  }, [pathname]);

  const loggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    loggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  const [trackKeeper, setTrackKeeper] = useState(0);
  const trackRef = useRef(trackKeeper);

  const incrementAndGet = () => {
    const nT = trackKeeper + 1;
    setTrackKeeper(nT);
    trackRef.current = nT;
    return nT;
  };

  function getItems() {
    const rights = user?.account.kasutusoigused?.map((oigus) => UserRoleType[oigus as keyof typeof UserRoleType]) || [];
    return getSidebarItems(rights, isLoggedIn);
  }

  function logOut() {
    setSelectedTopic(0);
    logout(navigate, toggleRefresh);
    setGlobalMenuItems(globalMenuItems);
  }

  const redirectToErrorPage = () => {
    return (
      <Routes>
        <Route path="*" element={<ErrorPage errorContainer={lastError} backFn={() => setLastError(undefined)} />} />
      </Routes>
    );
  };

  const [organisationSearch, setOrganisationSearch] = useState<{
    organisation?: Classifier;
  }>();
  const [contractSearch, setContractSearch] = useState<{
    message?: string;
  }>();
  const canToggle = !(getItems()[selectedTopic].topicItems.length === 0);
  return (
    <>
      <NavbarHeader
        personName={user ? user.account.nimi : ""}
        toggleSidebar={() => toggleSidebar(canToggle)}
        onLogout={logOut}
        onLogin={user && (user?.isDevloginEnabled ? () => navigate("/rollid2") : user.authenticate)}
        isLoggedIn={isLoggedIn}
        isLoading={isLoading}
      />

      <div className={"content-wrapper"}>
        <ScrollToTop />
        <GlobalMenu
          {...{
            globalMenuItems: currentGlobalMenuItems,
            selectGlobalMenuLink: selectGlobalMenuLink,
            setSidebarOpen,
            sidebarItems: getItems(),
            selectedTopic,
          }}
        />
        <Sidebar
          isOpen={isSidebarOpen}
          toggle={() => toggleSidebar(canToggle)}
          sidebarItems={getItems()}
          selectedTopic={selectedTopic}
        />
        {user && (
          <CurrentUserProvider
            value={{
              id: user.account.id,
              name: user.account.nimi,
              representationRights: user.account.esindusoigused.map((organisatsioon) => organisatsioon.id),
              representationRightClassifiers: user.account.esindusoigused.map((org) => {
                return { label: org.nimi, value: org.id.toString() };
              }),
              usageRights: user.account.kasutusoigused?.map(
                (oigus) => UserRoleType[oigus as keyof typeof UserRoleType],
              ),
              interestOrganisations: user.account.huviorganisatsioonid.map((organisatsioon) => organisatsioon.id),
              interestOrganisationClassifiers: user.account.huviorganisatsioonid.map((org) => {
                return { label: org.nimi, value: org.id.toString() };
              }),
              isLoggedIn:
                user.account.kasutusoigused.filter(
                  (x) => UserRoleType[x as keyof typeof UserRoleType] !== UserRoleType.HUVILINE,
                ).length > 0 || user.account.esindusoigused.length > 0,
              authenticate: user.authenticate,
              additionalConfiguration: additionalConf,
              infobuttonTexts: infotexts,
              publicConfig: publicConfig,
              updateInfotexts: updateInfotexts,
            }}
          >
            <SubviewProvider
              value={{
                organisationSearch,
                setOrganisationSearch,
                contractSearch,
                setContractSearch,
              }}
            >
              <Container fluid className="workarea">
                {lastError != undefined ? (
                  redirectToErrorPage()
                ) : (
                  <Routes>
                    <Route path="/:url*(/+)" element={<Navigate to={pathname.slice(0, -1)} />} />
                    <Route path="/account-does-not-exist" element={<Navigate to="/" replace />} />
                    <Route
                      path={`${ASSETS}/*`}
                      element={
                        <Suspense
                          fallback={
                            <Loader
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "50vh",
                              }}
                              color={"black"}
                              delay={0}
                            />
                          }
                        >
                          <AssetsRouting />
                        </Suspense>
                      }
                    ></Route>
                    <Route
                      path={`${PROCEEDINGS}/*`}
                      element={
                        <Suspense
                          fallback={
                            <Loader
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "50vh",
                              }}
                              color={"black"}
                              delay={0}
                            />
                          }
                        >
                          <ProceedingsRouting />
                        </Suspense>
                      }
                    ></Route>
                    <Route
                      path={`${CONTRACTS}/*`}
                      element={
                        <Suspense
                          fallback={
                            <Loader
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "50vh",
                              }}
                              color={"black"}
                              delay={0}
                            />
                          }
                        >
                          <ContractsContextProvider />
                        </Suspense>
                      }
                    ></Route>
                    <Route
                      path={`${REPORTS}/*`}
                      element={
                        <Suspense
                          fallback={
                            <Loader
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "50vh",
                              }}
                              color={"black"}
                              delay={0}
                            />
                          }
                        >
                          <ReportsRouting />
                        </Suspense>
                      }
                    ></Route>
                    <Route
                      path={`${REALESTATEUNIT}/*`}
                      element={
                        <Suspense
                          fallback={
                            <Loader
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "50vh",
                              }}
                              color={"black"}
                              delay={0}
                            />
                          }
                        >
                          <RealEstateUnitsSearchRouting />
                        </Suspense>
                      }
                    ></Route>
                    <Route
                      path={`${UNIT_MANAGEMENT}/*`}
                      element={
                        <Suspense
                          fallback={
                            <Loader
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "50vh",
                              }}
                              color={"black"}
                              delay={0}
                            />
                          }
                        >
                          <UnitManagementExtRouting />
                        </Suspense>
                      }
                    ></Route>
                    <Route
                      path={`${ADMINISTRATION}/*`}
                      element={
                        <Suspense
                          fallback={
                            <Loader
                              style={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "50vh",
                              }}
                              color={"black"}
                              delay={0}
                            />
                          }
                        >
                          <AdminRouting />
                        </Suspense>
                      }
                    ></Route>
                    <Route
                      path={`${NOTIFICATIONSSUBSCRIPTIONS}/*`}
                      element={isLoggedIn ? <NotificationsSubscription id={user.account.id} /> : <HomePageUnauth />}
                    ></Route>
                    {user?.isDevloginEnabled && (
                      <Route path="/rollid2" element={<Roles2 toggleRefresh={toggleRefresh} />}></Route>
                    )}
                    <Route path={"/sitemap"} element={<Sitemap loggedIn={isLoggedIn} />}></Route>
                    <Route path={"/sisukaart"} element={<Sitemap loggedIn={isLoggedIn} />}></Route>
                    <Route
                      path="/"
                      element={
                        !isLoading ? (
                          isLoggedIn ? (
                            <HomePageAuth />
                          ) : (
                            <HomePageUnauth userMissing={userMissingRef} />
                          )
                        ) : (
                          <div />
                        )
                      }
                    ></Route>
                    <Route path="*" element={<Navigate to="/" replace />}></Route>
                  </Routes>
                )}
              </Container>
            </SubviewProvider>
          </CurrentUserProvider>
        )}
        <SessionTimeoutModal isLoggedIn={loggedInRef} trackRef={trackRef} logOut={logOut} />
      </div>
    </>
  );
};

export default App;
